import { beautifyName } from "./utilities";
import { logError } from "./logError";
import { isAuth0RedirectSuppressedError } from "@/types/errors/Auth0RedirectSuppressedError";
import api from "@/api";
import { displayTime } from "./displayTime";

// LSP-16804: uppercase the first character of a phrase used at the START of a sentence. The
// entity references degrade to lowercase fallbacks ("a project" / "a session") when no name was
// stamped; when that phrase LEADS the title (no-actor update, no-actor share, default/archive)
// it must read "A project ...", not "a project ...". A real capitalized name is unaffected
// (uppercasing an already-uppercase first char is a no-op).
const capitalizeFirst = (phrase) => (phrase ? phrase.charAt(0).toUpperCase() + phrase.slice(1) : phrase);

export function getNotificationTitle(notification) {
	try {
		if (!notification || !notification.type) {
			return "Unknown notification";
		}
		switch (notification.type) {
			case "comment": {
				const comment = notification.metadata.commentCreated;
				if (!comment) {
					return "New comment";
				}
				const actor = comment.User ? beautifyName(comment.User.name) : null;
				if (comment.parentType === "comment") {
					// A reply. Name the owning project/session where the backend supplied it
					// ("...replied to your comment on Live call with Yasir #26657"); otherwise keep
					// the unenriched wording (LSP-16804 — degrade cleanly, never block).
					const location = getCommentLocationPhrase(notification);
					const suffix = location ? ` ${location}` : "";
					if (!actor) {
						return `A user replied to your comment${suffix}.`;
					}
					return `${actor} replied to your comment${suffix}.`;
				}
				// A comment on an artifact (video/photo) reads "on a <kind> in <owning project/session>"
				// — the backend stamps the owning name + artifactType for these (LSP-16804). A comment
				// directly on a project/session reads "on <project/session>".
				const commentTarget = getCommentTargetPhrase(notification, comment);
				if (!actor) {
					return `A user commented ${commentTarget}.`;
				}
				return `${actor} commented ${commentTarget}.`;
			}
			case "mention": {
				const comment = notification.metadata.commentCreated;
				if (!comment) {
					return "You were mentioned";
				}
				// beautifyName returns "" when the actor has no resolvable name (the sanitizer nulls
				// `name` when firstname+lastname are absent), so a present-but-nameless User would drop
				// the sentence's subject and render "mentioned you in a comment." with no name. Normalize
				// an empty/blank name to the same "A user" fallback the comment branch relies on. (LSP-16804)
				const resolvedActor = comment.User ? beautifyName(comment.User.name) : "";
				const actor = resolvedActor && resolvedActor.trim().length > 0 ? resolvedActor : "A user";
				// LSP-16804: name the owning project/session where available
				// ("...mentioned you in a comment on Acme Roofing"); otherwise keep the
				// unenriched "...in a comment" / "...in a reply".
				const location = getCommentLocationPhrase(notification);
				const suffix = location ? ` ${location}` : "";
				if (comment.parentType === "comment") {
					return `${actor} mentioned you in a reply${suffix}.`;
				}
				return `${actor} mentioned you in a comment${suffix}.`;
			}
			case "update": {
				const user = notification.metadata?.updatedBy;
				let difference = notification.metadata?.difference;
				difference = cleanDifference(difference);
				const entityName = getEntityDisplayName(notification);
				if (!user) {
					// System/no-actor update reads "<Name> was updated" (no preposition/field).
					// The entity phrase LEADS the sentence, so capitalize its fallback ("A project ...").
					return `${capitalizeFirst(entityName)} was updated.`;
				}
				const changedFields = difference ? getChangedFields(difference, notification.parentType) : [];
				if (changedFields.length === 0) {
					// Either nothing changed, or only internal/auto-managed fields changed (e.g. the
					// legacy lastActivityAt touch — LSP-15627). Name the entity without a field or
					// preposition ("Michael Adams updated Acme Roofing").
					return `${beautifyName(user.name)} updated ${entityName}.`;
				}
				// Preposition depends on WHAT changed (LSP-16804 / NOTIF-2): "in" if any changed field
				// is a child entity of the parent, else "for". A single SINGULAR entity field also takes
				// the article "a" ("updated a contact in ..."); multiple fields, an attribute, or an
				// already-plural noun take none — otherwise a plural relation diff (e.g. an unchanged
				// "Customers" array) would render the ungrammatical "a contacts".
				const hasEntityField = changedFields.some((field) => field.isEntity);
				const preposition = hasEntityField ? "in" : "for";
				const nouns = changedFields.map((field) => field.noun);
				const uniqueNouns = [...new Set(nouns)];
				const singleEntityNoun = uniqueNouns.length === 1 && changedFields[0].isEntity ? uniqueNouns[0] : null;
				const article = singleEntityNoun && !singleEntityNoun.endsWith("s") ? "a " : "";
				const fieldPhrase = joinFieldNouns(uniqueNouns);
				return `${beautifyName(user.name)} updated ${article}${fieldPhrase} ${preposition} ${entityName}.`;
			}
			case "share": {
				const user = notification.metadata?.updatedBy;
				// LSP-16804: name the shared project, e.g. "shared Acme Roofing with you as Editor".
				const shareTarget = getEntityDisplayName(notification);
				if (!user) {
					// The shared entity LEADS the sentence, so capitalize its fallback ("A project ...").
					return `${capitalizeFirst(shareTarget)} was shared with you.`;
				}
				return `${beautifyName(user.firstname)} shared ${shareTarget} with you as ${beautifyName(getAccessType(notification))}.`;
			}
			case "project_access_request": {
				// LSP-17773: a guest used "Ask to edit". The requester's email is stamped into
				// metadata.requesterEmail; the project name degrades to "a project" when absent.
				const requesterEmail =
					typeof notification.metadata?.requesterEmail === "string" ? notification.metadata.requesterEmail.trim() : "";
				const projectName = getProjectReference(notification.metadata);
				const requester = requesterEmail || "Someone";
				return `${requester} asked to edit ${projectName}.`;
			}
			default:
				// archive/create/delete/etc. — entity name + type, e.g. "Acme Roofing archived".
				// The entity phrase LEADS the sentence, so capitalize its fallback ("A session archived.").
				return `${capitalizeFirst(getEntityDisplayName(notification))} ${notification.type}.`;
		}
	} catch (error) {
		console.error("Failed to get notification title: ", error);
		return "Unknown notification";
	}
}

function cleanDifference(difference) {
	if (!difference) {
		return {};
	}
	if (Object.keys(difference).length == 1) {
		for (const key of Object.keys(difference)) {
			if (
				difference[key].source &&
				difference[key].target &&
				Array.isArray(difference[key].source) &&
				(difference[key].source.length - difference[key].target.length === 1 ||
					difference[key].source.length - difference[key].target.length === -1)
			) {
				if (key.charAt(key.length - 1) === "s") {
					difference[key.slice(0, -1)] = difference[key];
					delete difference[key];
				}
			}
		}
	}
	return difference;
}

// LSP-16804 / NOTIF-2: resolve the specific project/session a notification is about, using the
// name/number the backend now stamps into metadata (projectName / conversationName /
// conversationNumber). Older notifications created before this change have no such keys,
// so every branch degrades to a clean, non-empty fallback (never an empty bracket).
//
// For a conversation we prefer "<name> #<number>" (e.g. "Live call with Yasir #26657"),
// falling back to "Session #<number>" when only the number is known, the bare name when
// only the name is known, and "a session" when neither is present.
function getSessionReference(metadata) {
	const name = typeof metadata?.conversationName === "string" ? metadata.conversationName.trim() : "";
	const number = metadata?.conversationNumber;
	const hasNumber = number !== null && number !== undefined && `${number}`.trim().length > 0;
	if (name && hasNumber) {
		return `${name} #${number}`;
	}
	if (hasNumber) {
		return `Session #${number}`;
	}
	if (name) {
		return name;
	}
	return "a session";
}

// For a project we use the project name when present, else "a project".
function getProjectReference(metadata) {
	const name = typeof metadata?.projectName === "string" ? metadata.projectName.trim() : "";
	return name || "a project";
}

// The bare display name of the notification's entity (no preposition), used where the entity
// is the grammatical subject/object — e.g. update ("Michael Adams updated Acme Roofing"),
// share ("shared Acme Roofing with you") and the generic default ("Acme Roofing archived").
function getEntityDisplayName(notification) {
	if (notification.parentType === "conversation") {
		return getSessionReference(notification.metadata);
	}
	if (notification.parentType === "project") {
		return getProjectReference(notification.metadata);
	}
	return beautifyName(notification.parentType);
}

// LSP-16804 / NOTIF-2: the owning project/session phrase for a comment/mention/reply, woven with
// "on" — "on Acme Roofing" / "on Live call with Yasir #26657". Prefers the project name (the
// backend stamps projectName for project-owned comments and artifacts), else the session
// name/number, else null (caller degrades to the unenriched wording). Never returns "on a project"
// / "on a session" — an absent name means we simply omit the location rather than assert a
// misleading generic one.
function getCommentLocationPhrase(notification) {
	const metadata = notification?.metadata;
	const projectName = typeof metadata?.projectName === "string" ? metadata.projectName.trim() : "";
	if (projectName) {
		return `on ${projectName}`;
	}
	const sessionName = typeof metadata?.conversationName === "string" ? metadata.conversationName.trim() : "";
	const number = metadata?.conversationNumber;
	const hasNumber = number !== null && number !== undefined && `${number}`.trim().length > 0;
	if (sessionName && hasNumber) {
		return `on ${sessionName} #${number}`;
	}
	if (hasNumber) {
		return `on Session #${number}`;
	}
	if (sessionName) {
		return `on ${sessionName}`;
	}
	return null;
}

// Map a comment's artifact parentType to the human noun used in "commented on a <kind> in ...".
// Falls back to "an item" / "a file" when the kind is unknown (LSP-16804 — degrade cleanly).
function getArtifactKindNoun(comment, metadata) {
	const rawType = comment?.parentType ?? metadata?.artifactType;
	switch (rawType) {
		case "video":
		case "recording":
			return "video";
		case "photo":
			return "photo";
		case "artifact":
			return "an item";
		default:
			return "a file";
	}
}

// The phrase following "commented" for a comment on a project/session vs. an artifact:
//   - project/session parent → "in Acme Roofing" / "for/in <session>" via getCommentLocationPhrase
//     rendered as "on <name>" (comments read "commented on <project/session>").
//   - artifact parent (video/photo) → "on a video in <owning project/session>", degrading to
//     "on a video" when the owning name is unavailable (LSP-16804 — never blocks).
//
// LSP-16804: the notification's top-level parentType is now the artifact's OWNING project/session
// (so watchers resolve and delivery works — the ARTIFACT parentType was silently dropped by the
// socket plugin). The artifact IDENTITY therefore lives in metadata.artifactType (and the preserved
// commentCreated.parentType), NOT in notification.parentType. Key the artifact copy on EITHER —
// artifactType is the durable signal.
function getCommentTargetPhrase(notification, comment) {
	const parentType = comment?.parentType;
	const artifactType = notification?.metadata?.artifactType;
	const isArtifactComment =
		parentType === "video" ||
		parentType === "recording" ||
		parentType === "photo" ||
		parentType === "artifact" ||
		artifactType === "video" ||
		artifactType === "photo" ||
		artifactType === "artifact";
	if (isArtifactComment) {
		const kind = getArtifactKindNoun(comment, notification?.metadata);
		// "an item" already includes its article; "video"/"photo"/"a file" need "a"/none respectively.
		const artifactRef = kind === "an item" || kind === "a file" ? kind : `a ${kind}`;
		const location = getCommentLocationPhrase(notification);
		return location ? `on ${artifactRef} ${location.replace(/^on /, "in ")}` : `on ${artifactRef}`;
	}
	// Direct comment on a project/session — "on Acme Roofing" / "on Live call with Yasir #26657".
	if (notification.parentType === "conversation") {
		return `on ${getSessionReference(notification.metadata)}`;
	}
	if (notification.parentType === "project") {
		return `on ${getProjectReference(notification.metadata)}`;
	}
	return `on ${beautifyName(notification.parentType)}`;
}

// LSP-16804 / NOTIF-2: entity fields — a change to one of these is a change to a CHILD ENTITY of
// the parent (contact/user/address/artifact/file relation), which drives the preposition to "in"
// and the article to "a" for a single such change. Nouns are LOWERCASE in the new copy. Keys cover
// the lowercase foreign-key columns AND the capitalized association keys that appear in project
// diffs (Customers/Customer/User/Users). The map need not be exhaustive — anything not listed is
// treated as an ATTRIBUTE and humanized.
const ENTITY_FIELD_NOUNS = {
	customerId: "contact",
	workflowCustomerId: "contact",
	Customers: "contacts",
	Customer: "contact",
	forcedUserId: "user",
	User: "user",
	Users: "user",
	addressId: "address"
};

// LSP-15627 / NOTIF-2: human-readable LOWERCASE labels for the ATTRIBUTE field names that can
// appear in an "update" notification diff. Without these, raw Sequelize column names leak into the
// UI (e.g. "lastActivityAt" rendered as "LastActivityAt"). The map need not be exhaustive —
// anything not listed falls through to humanizeFieldName.
const FIELD_LABELS = {
	name: "name",
	number: "number",
	status: "status",
	notes: "notes",
	type: "type",
	projectId: "project",
	conversationId: "session",
	initiationType: "initiation type",
	sourceType: "source",
	inboundStatus: "inbound status",
	notificationTemplate: "notification template",
	instructionId: "instruction",
	kioskId: "kiosk",
	visitorId: "visitor",
	qrCodeId: "QR code",
	screenShareSupported: "screen share",
	recordingSupported: "recording",
	autoStartRecordings: "auto-start recording",
	usesAppRecording: "app recording",
	followupRequired: "follow-up",
	followupRequestedDate: "follow-up date",
	followupNotes: "follow-up notes",
	followUpRequestedBy: "follow-up requested by"
};

// Internal/auto-managed fields that are not user-meaningful and must never surface in a
// notification. lastActivityAt is stripped server-side now (LSP-15627), but legacy
// notifications still carry it, so it stays here defensively alongside the other plumbing
// fields (idempotency keys, audit columns, and the VIRTUAL computed columns).
const HIDDEN_FIELDS = new Set([
	"lastActivityAt",
	"thumbnailSnapshotId",
	"idempotencyId",
	"idempotencyRequestHash",
	"deletedBy",
	"sourceTypeDisplay",
	"isConcierge",
	"isContact"
]);

// Fallback for any field not in FIELD_LABELS: drop the trailing technical "At"/"Id" suffix, split
// camelCase/PascalCase into words, then lower case (NOTIF-2 — field nouns are lowercased).
// e.g. "someNewFieldAt" -> "some new field". Guarantees a future column added to a model degrades
// to readable copy instead of a raw name.
function humanizeFieldName(key) {
	const words = key
		.replace(/(At|Id)$/, "")
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
		.split(/[\s_]+/)
		.filter((word) => word.length > 0);
	if (words.length === 0) {
		return key;
	}
	return words.map((word) => word.toLowerCase()).join(" ");
}

// LSP-16804 / NOTIF-2: classify each changed field as an ENTITY (child-entity relation → "in" +
// "a") or an ATTRIBUTE (→ "for", no article) and resolve its lowercase display noun. Returns the
// de-duplicated list of { noun, isEntity } in stable diff order.
function getChangedFields(difference, parentType) {
	const fields = Object.keys(difference)
		// Project diffs carry the project's associations as capitalized keys ("Customers"/"User"), which we
		// keep and label. Conversation diffs instead carry the lowercase foreign-key columns
		// (customerId/workflowCustomerId) that already represent the same change AND the capitalized
		// association object (e.g. "WorkflowCustomer"); for conversations we drop every capitalized key via
		// the lowercase-first-char test below, since the lowercase FK already covers it and the object is
		// redundant.
		.filter((key) => !HIDDEN_FIELDS.has(key) && (parentType === "project" || key.charAt(0) === key.charAt(0).toLowerCase()))
		.map((key) => {
			if (Object.prototype.hasOwnProperty.call(ENTITY_FIELD_NOUNS, key)) {
				return { noun: ENTITY_FIELD_NOUNS[key], isEntity: true };
			}
			return { noun: FIELD_LABELS[key] ?? humanizeFieldName(key), isEntity: false };
		});
	// De-duplicate by noun (keep the first occurrence so isEntity is preserved).
	const seen = new Set();
	return fields.filter((field) => {
		if (seen.has(field.noun)) {
			return false;
		}
		seen.add(field.noun);
		return true;
	});
}

// Join field nouns for the sentence: "status", "status and notes", "status, notes and contact".
function joinFieldNouns(nouns) {
	if (nouns.length > 1) {
		const lastTwo = nouns.slice(-2);
		return nouns.slice(0, -2).concat(`${lastTwo[0]} and ${lastTwo[1]}`).join(", ");
	}
	return nouns.join(", ");
}

function getAccessType(notification) {
	if (notification.type === "share") {
		return beautifyName(notification.metadata.projectShare.accessType);
	}
	return null;
}

export function getNotificationIcon(notification) {
	// LSP-16804: an artifact comment now carries the OWNING project/conversation as its top-level
	// parentType (so the socket plugin can resolve watchers — an "artifact" parentType was silently
	// dropped). Detect an artifact comment by its metadata.artifactType (or the preserved
	// commentCreated.parentType) so it still shows the photo/video icon rather than the owner's icon.
	const artifactComment = getArtifactCommentDescriptor(notification);
	if (artifactComment) {
		return artifactComment === "photo" ? "icon-image" : "icon-ls-video-logo";
	}
	switch (notification.parentType) {
		case "project":
			return "icon-form";
		case "artifact": {
			const comment = notification.metadata.commentCreated;
			if (!comment) {
				return "icon-table-stroke";
			}
			if (comment.parentType === "photo") {
				return "icon-image";
			}
			return "icon-ls-video-logo";
		}
		case "comment":
			return "icon-comment-stroke";
		case "conversation":
			return "icon-conversation-solid";
		default:
			return "icon-table-stroke";
	}
}

// Return the artifact "kind" ("video" | "photo" | "artifact") when this notification is a comment on
// an artifact, else null. Reads metadata.artifactType (durable, stamped at creation) with a fallback
// to the preserved commentCreated.parentType. Used by getNotificationIcon so the artifact icon no
// longer depends on the (now project/conversation) top-level parentType (LSP-16804).
function getArtifactCommentDescriptor(notification) {
	if (notification?.type !== "comment") {
		return null;
	}
	const artifactType = notification?.metadata?.artifactType;
	if (artifactType === "video" || artifactType === "recording" || artifactType === "photo" || artifactType === "artifact") {
		return artifactType === "recording" ? "video" : artifactType;
	}
	const commentParentType = notification?.metadata?.commentCreated?.parentType;
	if (
		commentParentType === "video" ||
		commentParentType === "recording" ||
		commentParentType === "photo" ||
		commentParentType === "artifact"
	) {
		return commentParentType === "recording" ? "video" : commentParentType;
	}
	return null;
}

// LSP-19483: the panel showed ONLY relative time with no exact date-time anywhere, while the
// notification's own title embedded an absolute one. Both now come from the shared rule.
export function getNotificationTime(notification) {
	return displayTime(notification.createdAt).label;
}

/** The full date-time for the row's tooltip, so the exact moment is always reachable. */
export function getNotificationExactTime(notification) {
	return displayTime(notification.createdAt).exactLabel;
}

export const clickNotificationMixin = {
	methods: {
		async clickNotification(notification) {
			// redirect based on the notification type
			const currentOrgId = await api.getOrganizationId();
			let comment = null;
			try {
				await api.updateNotification(notification.id, { isRead: true });
				// LSP-17773: a request-access notification opens the projects LIST with the share
				// dialog pre-staged for the requester's email — mirroring the request email's deep
				// link (?share=<projectId>&requestAccessEmail=<email>), which ProjectsPage reads in
				// maybeOpenShareFromQuery — rather than the project detail page the "project"
				// parentType would otherwise route to.
				if (notification.type === "project_access_request") {
					const requesterEmail =
						typeof notification.metadata?.requesterEmail === "string" ? notification.metadata.requesterEmail : "";
					const stagedParam = requesterEmail ? `&requestAccessEmail=${encodeURIComponent(requesterEmail)}` : "";
					return `/projects?share=${notification.parentId}${stagedParam}`;
				}
				if (notification.type === "mention" || notification.type === "comment") {
					comment = notification.metadata.commentCreated;
					try {
						comment = await api.getComment(comment.id);
						if (comment.deletedAt) {
							await this.openNotFoundDialog("Comment");
							return;
						}
					} catch (error) {
						//TODO: Sindhuja, R66: handle other errors
						await this.openNotFoundDialog("Comment", error);
						return;
					}
				}
				let url = "";
				let addCommentFragment = false;
				// LSP-15865: only comment/mention/reply notifications should deep-link to the
				// Comments tab. Project SHARE/UPDATE notifications share the "project" parentType
				// but must open the project's default tab, not Comments (the `#comments` fragment
				// drives EntityDetailPage.handleHashNavigation's tab selection on both platforms).
				const isCommentNotification =
					notification.type === "mention" || notification.type === "comment" || notification.type === "reply";
				//TODO: Sindhuja, R66: add unit tests to to test all of the different permutations
				switch (getArtifactCommentDescriptor(notification) ? "artifact" : notification.parentType) {
					case "project": {
						url = await this.getUrlForProject(notification.parentId, `/projects/${notification.parentId}`, notification.id);
						addCommentFragment = isCommentNotification;
						break;
					}
					case "conversation": {
						try {
							const conversation = await api.getConversationInsecure(notification.parentId);
							if (conversation.organizationId !== currentOrgId) {
								return await this.openSwitchOrganizationDialog(
									conversation.organizationId,
									`/conversations/${conversation.id}#comments`
								);
							}
						} catch (error) {
							//TODO: Sindhuja, R66: handle other errors
							await this.openNotFoundDialog("Session", error);
							return;
						}
						// LSP-16087: mirror the project case above — only comment/mention/reply
						// notifications deep-link to the Comments tab. Other conversation
						// notifications (update/status/assign/share/…) must open the session's
						// default view, not Comments.
						url = `/conversations/${notification.parentId}`;
						addCommentFragment = isCommentNotification;
						break;
					}
					// Artifact comments use the owning entity for delivery, but clicks open the artifact.
					case "artifact":
					case "mention":
					case "comment": {
						const conversationId = comment.conversationId ?? (notification.parentType === "conversation" ? notification.parentId : null);
						let orgId = null;
						let conversation = null;
						if (conversationId) {
							try {
								conversation = await api.getConversationInsecure(conversationId);
								orgId = conversation.organizationId;
							} catch (error) {
								orgId = null;
							}
						}
						const projectId = conversation?.projectId ?? comment.projectId;
						if (!orgId) {
							orgId = comment.organizationId;
						}
						switch (comment.parentType) {
							case "conversation": {
								if (conversationId && !conversation) {
									await this.openNotFoundDialog("Session");
									return;
								} else if (!conversationId) {
									try {
										conversation = await api.getConversationInsecure(comment.parentId);
										if (conversation.organizationId !== currentOrgId) {
											return await this.openSwitchOrganizationDialog(
												conversation.organizationId,
												`/conversations/${comment.parentId}#comments`
											);
										}
									} catch (error) {
										//TODO: Sindhuja, R66: handle other errors
										await this.openNotFoundDialog("Session", error);
										return;
									}
								}
								if (orgId !== currentOrgId) {
									return await this.openSwitchOrganizationDialog(orgId, `/conversations/${comment.parentId}#comments`);
								}
								url = `/conversations/${comment.parentId}`;
								addCommentFragment = true;
								break;
							}
							case "project":
								url = await this.getUrlForProject(comment.parentId, `/projects/${comment.parentId}`, notification.id);
								addCommentFragment = true;
								break;
							case "artifact":
							case "video":
							case "recording":
							case "photo": {
								if (projectId) {
									url = await this.getUrlForProject(
										projectId,
										`/projects/${projectId}?artifactId=${comment.parentId}&artifactType=${comment.parentType}`,
										notification.id
									);
								} else if (conversationId) {
									if (!conversation) {
										await this.openNotFoundDialog("Session");
										return;
									}
									if (orgId !== currentOrgId) {
										return await this.openSwitchOrganizationDialog(
											orgId,
											`/conversations/${conversationId}?artifactId=${comment.parentId}&artifactType=${comment.parentType}`
										);
									}
									url = `/conversations/${conversationId}?artifactId=${comment.parentId}&artifactType=${comment.parentType}`;
								}
								break;
							}
							case "comment": {
								const originalComment = await api.getComment(comment.parentId);
								let originalConversationId = originalComment.conversationId;
								if (originalComment.parentType === "conversation") {
									originalConversationId = originalComment.parentId;
								}
								let originalProjectId = null;
								let originalOrgId = null;
								if (originalConversationId) {
									try {
										const originalConversation = await api.getConversationInsecure(originalConversationId);
										originalProjectId = originalConversation.projectId;
										originalOrgId = originalConversation.organizationId;
									} catch (error) {
										//TODO: Sindhuja, R66: handle other errors
										await this.openNotFoundDialog("Session", error);
										return;
									}
								}
								if (!originalOrgId) {
									originalOrgId = originalComment.organizationId;
								}
								switch (originalComment.parentType) {
									case "conversation":
										if (originalOrgId !== currentOrgId) {
											return await this.openSwitchOrganizationDialog(
												originalOrgId,
												`/conversations/${originalComment.parentId}#comments`
											);
										}
										url = `/conversations/${originalComment.parentId}`;
										addCommentFragment = true;
										break;
									case "project":
										url = `/projects/${originalComment.parentId}`;
										addCommentFragment = true;
										break;
									case "video":
									case "recording":
									case "photo":
										{
											if (originalProjectId) {
												url = await this.getUrlForProject(
													originalProjectId,
													`/projects/${originalProjectId}?artifactId=${originalComment.parentId}&artifactType=${originalComment.parentType}`,
													notification.id
												);
											} else if (originalConversationId) {
												if (originalOrgId !== currentOrgId) {
													return await this.openSwitchOrganizationDialog(
														originalOrgId,
														`/conversations/${originalConversationId}?artifactId=${originalComment.parentId}&artifactType=${originalComment.parentType}`
													);
												}
												url = `/conversations/${originalConversationId}?artifactId=${originalComment.parentId}&artifactType=${originalComment.parentType}`;
											}
										}
										break;
									default:
										url = await this.getUrlForProject(
											originalComment.parentId,
											`/projects/${originalComment.parentId}`,
											notification.id
										);
										addCommentFragment = true;
										break;
								}
								break;
							}
							default:
								if (conversationId) {
									url = `/conversations/${conversationId}?artifactId=${comment.parentId}&artifactType=${comment.parentType}`;
								}
								break;
						}
						if (!url) {
							console.warn("Cannot build comment notification URL", {
								notificationId: notification.id,
								commentId: comment.id,
								parentType: comment.parentType,
								projectId,
								conversationId
							});
						}
						break;
					}
					default:
						break;
				}

				if (addCommentFragment) {
					url += "#comments";
				}
				return url;
			} catch (error) {
				console.error("Failed to redirect to notification: ", error);
			}
		},
		async getUrlForProject(projectId, baseUrl, notificationId) {
			try {
				const shareIdResponse = await api.getShareIdForProject(projectId);
				if (!shareIdResponse) {
					await this.openNotFoundDialog("Project");
					return null;
				}
				const shareId = shareIdResponse.shareId;
				if (baseUrl.includes("?")) {
					return `${baseUrl}&shareId=${shareId}`;
				}
				return `${baseUrl}?shareId=${shareId}`;
			} catch (error) {
				console.error(error, {
					tags: {
						method: "getUrlForProject",
						file: "notifications"
					},
					extra: {
						projectId,
						baseUrl,
						notificationId
					}
				});
				await this.openNotFoundDialog("Project", error);
				return null;
			}
		},
		async openSwitchOrganizationDialog(organizationId, path) {
			const output = await this.$root.$confirm.open(
				"Switch Organization to View",
				"The notification you are trying to view requires you to switch organizations to view.",
				null,
				"Switch Organization",
				"Cancel"
			);
			if (!output) {
				return;
			}
			try {
				// EXPLICIT: the user just clicked "Switch Organization" in the dialog above, so this
				// switch must never be silently dropped by the bfcache-restore latch (LSP-17342).
				await api.setOrganizationId(organizationId, { isExplicitUserIntent: true });
			} catch (orgSwitchError) {
				// LSP-17342: setOrganizationId's redirect fallback REJECTS instead of hanging
				// forever. Without this catch that is an unhandled rejection, and worse, the
				// navigation below would run having NOT switched org — showing the notification's
				// target in the wrong organization.
				logError("notifications.openSwitchOrganizationDialog: org switch failed", orgSwitchError, {
					organizationId,
					suppressedByRestoreRecovery: isAuth0RedirectSuppressedError(orgSwitchError)
				});
				if (isAuth0RedirectSuppressedError(orgSwitchError)) {
					// DEFENSIVE, and currently not reachable in production: this call site passes
					// isExplicitUserIntent: true, so setOrganizationId can never throw the suppressed
					// error here — suppression only applies to automatic redirects. It is kept so the
					// two causes stay distinguishable if a future caller does classify this switch
					// as automatic — with the alternative being that such a change silently starts
					// showing a failure dialog on a document that is navigating away. The test for
					// this branch injects the error directly and is honest about that.
					return;
				}
				// The user asked to switch organizations and it did not happen. Previously the
				// dialog just closed, which is indistinguishable from "Back" — the user is left
				// believing they switched, still in the old org, with the notification unopened.
				await this.openOrgSwitchFailedDialog();
				return;
			}
			if (path) {
				// Use the SPA router so this navigation coordinates with
				// useOrgRouteGuard's redirect (which fires on the same org-id
				// change). A second router.push aborts an in-flight replace,
				// so the user reliably lands at `path` instead of racing the
				// guard's list-page redirect via a hard window.location.href.
				if (this.$router && typeof this.$router.push === "function") {
					this.$router.push(path).catch(() => {
						// NavigationDuplicated / aborted-by-guard are benign here.
					});
				} else {
					window.location.href = path;
				}
			}
		},
		async openOrgSwitchFailedDialog() {
			// LSP-17342: a user-initiated org switch that failed has to be visible. The generic
			// openNotFoundDialog below is wrong here — nothing was deleted; the switch itself did
			// not happen, and retrying is the useful next step.
			await this.$root.$confirm.open(
				"Could not switch organization",
				"We could not switch you to the organization this notification belongs to. Please try again in a moment.",
				null,
				"OK",
				null,
				false,
				true
			);
		},
		async openNotFoundDialog(entity, error = null) {
			await this.$root.$confirm.open(
				`${entity} has been deleted`,
				`The ${entity.toLowerCase()} you are trying to view has been deleted and is no longer available.`,
				null,
				"OK",
				null,
				false,
				true
			);
			if (error) {
				console.error(error, {
					tags: {
						method: "clickNotification",
						file: "notifications"
					},
					extra: {
						entity: entity
					}
				});
			}
		}
	}
};

export async function readNotification(notification) {
	await api.updateNotification(notification.id, { isRead: true });
}
